/**
 * The one Panel calendar module. Instants stay instants (ISO UTC on the
 * wire); every restaurant date is a day in Europe/Madrid, including its 23-
 * and 25-hour days at the clock changes
 * (docs/decisions/2026-09-28-spain-only-eur-and-madrid.md). The device's own
 * time zone is never used. A calendar day is a `YYYY-MM-DD` key; date-only
 * arithmetic runs on a UTC scratch date inside this module, never as
 * `instant + 86_400_000`.
 */
import { serviceDateOf } from '@exylon/generated-contract-kernel/calendar';

/** @internal The wire label specs and fixtures still carry. */
export const RESTAURANT_TIME_ZONE = 'Europe/Madrid';

/**
 * The Spanish weekday names, Sunday first: the index is `Date#getUTCDay()` of a calendar day
 * (0 = domingo … 6 = sábado); an ISO weekday (1 = lunes … 7 = domingo) reads `[isoWeekday % 7]`.
 * `short` is the three-letter chart tick, `plural` the «los sábados» form.
 */
export const WEEKDAY_NAMES_ES = Object.freeze([
  Object.freeze({ long: 'domingo', short: 'dom', plural: 'domingos' }),
  Object.freeze({ long: 'lunes', short: 'lun', plural: 'lunes' }),
  Object.freeze({ long: 'martes', short: 'mar', plural: 'martes' }),
  Object.freeze({ long: 'miércoles', short: 'mié', plural: 'miércoles' }),
  Object.freeze({ long: 'jueves', short: 'jue', plural: 'jueves' }),
  Object.freeze({ long: 'viernes', short: 'vie', plural: 'viernes' }),
  Object.freeze({ long: 'sábado', short: 'sáb', plural: 'sábados' }),
] as const);

/**
 * The Spanish month names, January first: the index is the calendar month minus one. Word for word the kernel's
 * `SPANISH_MONTH_NAMES` (its spec holds them equal); written here because importing the kernel's day words into this
 * module would carry them into the Panel's entry chunk.
 */
export const MONTH_NAMES_ES = Object.freeze([
  Object.freeze({ long: 'enero', short: 'ene' }),
  Object.freeze({ long: 'febrero', short: 'feb' }),
  Object.freeze({ long: 'marzo', short: 'mar' }),
  Object.freeze({ long: 'abril', short: 'abr' }),
  Object.freeze({ long: 'mayo', short: 'may' }),
  Object.freeze({ long: 'junio', short: 'jun' }),
  Object.freeze({ long: 'julio', short: 'jul' }),
  Object.freeze({ long: 'agosto', short: 'ago' }),
  Object.freeze({ long: 'septiembre', short: 'sept' }),
  Object.freeze({ long: 'octubre', short: 'oct' }),
  Object.freeze({ long: 'noviembre', short: 'nov' }),
  Object.freeze({ long: 'diciembre', short: 'dic' }),
] as const);

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const HOUR_MS = 3_600_000;
const SERVICE_DAY_START_HOUR = 4;
const formats = new Map<string, Intl.DateTimeFormat>();

function formatter(options: Intl.DateTimeFormatOptions, timeZone: string, locale = 'es-ES'): Intl.DateTimeFormat {
  const key = JSON.stringify([locale, timeZone, options]);
  let format = formats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, { ...options, timeZone });
    formats.set(key, format);
  }
  return format;
}

function instantMs(instant: number | string | Date): number {
  const value = typeof instant === 'number' ? instant : instant instanceof Date ? instant.getTime() : Date.parse(instant);
  if (!Number.isFinite(value)) throw new RangeError('La fecha no está disponible.');
  return value;
}

/** A real calendar day key («2026-02-29» is not). */
export function isCalendarDay(value: unknown): value is string {
  if (typeof value !== 'string' || !DAY_KEY.test(value)) return false;
  const scratch = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(scratch.getTime()) && scratch.toISOString().slice(0, 10) === value;
}

function dayScratch(day: string): Date {
  if (!isCalendarDay(day)) throw new RangeError('La fecha no es válida.');
  return new Date(`${day}T00:00:00.000Z`);
}

/** The Madrid calendar day of an instant; throws on an unreadable instant. */
export function madridDay(instant: number | string | Date): string {
  return formatter({ year: 'numeric', month: '2-digit', day: '2-digit' }, RESTAURANT_TIME_ZONE, 'en-CA').format(instantMs(instant));
}

/** The Madrid calendar day of an instant, or null when it cannot be read. */
export function madridDayOrNull(instant: number | string | Date | null | undefined): string | null {
  if (instant === null || instant === undefined) return null;
  try { return madridDay(instant); } catch { return null; }
}

/**
 * The service day of an instant: its Madrid day, or the day before while the Madrid clock is before 04:00
 * (docs/decisions/2026-10-08-one-service-day-definition.md). At 01:30 the service day is still the night before's. It
 * is the Backend's own `serviceDateOf`, read from the shared business clock. Throws on an unreadable instant.
 */
export function madridServiceDay(instant: number | string | Date): string {
  return serviceDateOf(new Date(instantMs(instant)));
}

/** `day` plus `days` calendar days (negative goes back). */
export function addCalendarDays(day: string, days: number): string {
  if (!Number.isSafeInteger(days)) throw new RangeError('El número de días no es válido.');
  const scratch = dayScratch(day);
  scratch.setUTCDate(scratch.getUTCDate() + days);
  return scratch.toISOString().slice(0, 10);
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export function calendarDaysBetween(from: string, to: string): number {
  return Math.round((dayScratch(to).getTime() - dayScratch(from).getTime()) / (24 * HOUR_MS));
}

/** The first day of the month `months` away from the month of `day` («2026-03-18», −2 → «2026-01-01»). */
export function calendarMonthStart(day: string, months = 0): string {
  if (!Number.isSafeInteger(months)) throw new RangeError('El número de meses no es válido.');
  const scratch = dayScratch(day);
  return new Date(Date.UTC(scratch.getUTCFullYear(), scratch.getUTCMonth() + months, 1)).toISOString().slice(0, 10);
}

/** The instant (epoch ms) at which a Madrid calendar day begins. */
export function madridDayStart(day: string): number {
  const utcMidnight = dayScratch(day).getTime();
  // Madrid is UTC+1 or UTC+2; its clock changes are never at midnight.
  for (const offset of [HOUR_MS, 2 * HOUR_MS]) {
    const candidate = utcMidnight - offset;
    if (madridDay(candidate) === day && madridDay(candidate - 1) !== day) return candidate;
  }
  throw new RangeError('La fecha no existe en el calendario del restaurante.');
}

/**
 * The instant (epoch ms) at which a service day begins: 04:00 on the Madrid clock of that day
 * (docs/decisions/2026-10-08-one-service-day-definition.md). Madrid's clock changes happen before 04:00, so the
 * offset at 04:00 is the day's new one. The Backend's twin is `service.cash_drawer_day_start`.
 */
export function madridServiceDayStart(day: string): number {
  const fourUtc = dayScratch(day).getTime() + SERVICE_DAY_START_HOUR * HOUR_MS;
  for (const offset of [HOUR_MS, 2 * HOUR_MS]) {
    const candidate = fourUtc - offset;
    if (madridDay(candidate) === day && madridClock(candidate) === '04:00') return candidate;
  }
  throw new RangeError('La fecha no existe en el calendario del restaurante.');
}

/** A Madrid calendar day as a half-open instant interval: 23, 24 or 25 hours. */
export function madridDayBounds(day: string): Readonly<{ start: number; endExclusive: number }> {
  return { start: madridDayStart(day), endExclusive: madridDayStart(addCalendarDays(day, 1)) };
}

/** A date-only label («18 de marzo de 2026»): the day is a calendar fact, not an instant. */
export function formatCalendarDay(day: string, options: Intl.DateTimeFormatOptions): string {
  return formatter(options, 'UTC').format(new Date(`${dayScratch(day).toISOString().slice(0, 10)}T12:00:00.000Z`));
}

/** A calendar day as people say it: the weekday and the day of the month («jueves 8»). */
export function weekdayDayText(day: string): string {
  return `${WEEKDAY_NAMES_ES[new Date(`${day}T00:00:00.000Z`).getUTCDay()]!.long} ${Number(day.slice(8, 10))}`;
}

/**
 * A span of calendar days in words: «hoy» (one day, and it is `today`), «el 12 de marzo», «del 12 al 14 de marzo» or
 * «del 30 de marzo al 2 de abril». Null when either day is not a calendar day or the span runs backwards.
 */
export function calendarDaySpanText(from: unknown, to: unknown, today: string | null): string | null {
  if (!isCalendarDay(from) || !isCalendarDay(to) || to < from) return null;
  const dayMonth = (day: string): string => formatCalendarDay(day, { day: 'numeric', month: 'long' });
  if (from === to) return from === today ? 'hoy' : `el ${dayMonth(from)}`;
  return `del ${from.slice(0, 7) === to.slice(0, 7) ? Number(from.slice(8)) : dayMonth(from)} al ${dayMonth(to)}`;
}

/** An instant shown on the Madrid clock; throws on an unreadable instant. */
export function formatMadridInstant(instant: number | string | Date, options: Intl.DateTimeFormatOptions): string {
  return formatter(options, RESTAURANT_TIME_ZONE).format(instantMs(instant));
}

/** «14:05» on the Madrid clock; throws on an unreadable instant. */
export function madridClock(instant: number | string | Date): string {
  return formatMadridInstant(instant, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}
